/**
 * @module components/Chart/ChartHazard
 * @author Charles Blais <charles.blais@canada.ca>
 */

import { useMemo, useState } from 'react';
import ReactEcharts from 'echarts-for-react';

import { useLanguageState } from '../../contexts/language-state-provider';
import { useAppState } from '../../contexts/app-state-provider';

import {
    getToolbox, getGrid, getLegend, getTooltip,
    getXAxis, getYAxis, getDataZoom, getOnEvents,
    saToNumber, getSubtext, markerIcon,
    hideSaFromSiteDesignations,
    hidePGAFromSiteDesignations,
} from './lib';

type Props = {
  variables: HazardParameters,
  siteDesignations: SiteDesignation[],
  sa?: string[],
  initialLog?: boolean,
  initialSelected?: string[],
}


/**
 * Convert the site designations list into a list
 * that is echart friendly.
 * 
 * @param {*} siteDesignations
 * @param {*} selectedSas 
 */
const makeSeries = (
  siteDesignations: SiteDesignation[],
  selectedSas: string[],
) => {
  const series = selectedSas.map((selectedSa) => {
    const value = saToNumber(selectedSa);

    const hiddenSa = hideSaFromSiteDesignations(selectedSa, siteDesignations);

    return {
      name: `Sa(${value})`,
      type: 'line',
      symbol: markerIcon,
      symbolSize: 10,
      // @ts-ignore: we know the key will exist
      data: hiddenSa.map((site) => [site[selectedSa], site.foe])
    }
  });

  const hiddenPGA = hidePGAFromSiteDesignations(siteDesignations);

  const seriesPGAandPGV = [{
    name: `PGA (g)`,
    type: 'line',
    symbol: markerIcon,
    symbolSize: 10,
    data: hiddenPGA.map((site) => [site.pga, site.foe])
  }];

  return series.concat(seriesPGAandPGV);
};


/**
 * React component
 * ===============
 */
export const ChartHazard = ({
  variables,
  siteDesignations,
  sa = [
    'sa0p05',
    'sa0p1',
    'sa0p2',
    'sa0p3',
    'sa0p5',
    'sa1p0',
    'sa2p0',
    'sa5p0',
    'sa10p0',
  ],
  initialLog = true,
  initialSelected = [
    'Sa(0.2)',
    'Sa(0.5)',
    'Sa(1)',
    'Sa(2)',
    'Sa(5)',
    'Sa(10)',
  ],
}: Props) => {
  const [ lang ] = useLanguageState();
  const [ state ] = useAppState();

  const [ log, setLog ] = useState(initialLog);
  // save a state for legend selection
  const [ selected, setSelected ] = useState(initialSelected);

  const series = useMemo(() => makeSeries(siteDesignations, sa), [siteDesignations, sa]);

  const subtext = getSubtext(variables, log, lang);

  return (
    <ReactEcharts
      style={{ height: 600 }}
      onEvents={getOnEvents(setSelected)}
      option={{
        title: {
          text: lang.chart.hazardCurve,
          subtext,
          textStyle: {
            fontSize: 20,
          }
        },
        color: state.chart.colorTable,
        tooltip: getTooltip(),
        legend: getLegend(series, selected),
        grid: getGrid(),
        toolbox: getToolbox(lang, log, setLog),
        xAxis: getXAxis(lang.chart.gm, log),
        yAxis: getYAxis(lang.chart.probability, log),
        dataZoom: getDataZoom(),
        series,
      }}
    />
  )
};


export default ChartHazard;
